import UniformSync from '@/uniform/UniformSync';
import EquipmentSync from '@/equipment/EquipmentSync';
import TopLoadingBar, { LoadingFallback } from '@/components/TopLoadingBar';
import LiveUpdates from '@/components/native/LiveUpdates';
import SignInApproval from '@/components/SignInApproval';
import { Suspense, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import './App.css'
import { Toaster } from "@/components/ui/toaster"
import { Toaster as HotToaster } from "react-hot-toast"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import VisualEditAgent from '@/lib/VisualEditAgent'
import NavigationTracker from '@/lib/NavigationTracker'
import { pagesConfig } from './pages.config'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import SetPassword from './pages/SetPassword';
import ForgotPassword from './pages/ForgotPassword';
import Login from './pages/Login';
import Register from './pages/Register';
import PendingApproval from './pages/PendingApproval';
import VerifyEmail from './pages/VerifyEmail';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Support from './pages/Support';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { isAppLayout } from '@/lib/native';
import { BrandingProvider } from '@/lib/BrandingContext';
import { PortalProvider } from '@/lib/PortalContext';
import { setupNativeAuthListener } from '@/lib/nativeAuth';

import { StaffProfileProvider } from '@/lib/StaffProfileContext';
import { useNavigate } from 'react-router-dom';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;
const EventDetailsPage = Pages["event-details"];
const StaffProfilePage = Pages["staff-profile"];

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>{children}</Layout>
  : <>{children}</>;

function NavigateSetter() {
  const navigate = useNavigate();
  useEffect(() => { window.__navigate = navigate; }, [navigate]);
  return null;
}

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, isAuthenticated, navigateToLogin, checkAppState, user } = useAuth();

  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-[rgb(var(--brand-yellow))] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (authError?.type === 'auth_unavailable') return <div role="alert" className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
    <p>{authError.message}</p><button className="rounded-md bg-primary px-4 py-2 text-primary-foreground" onClick={checkAppState}>Try again</button>
  </div>;

  if (!isAuthenticated) {
    navigateToLogin();
    return null;
  }

  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    }
  }

  if (user?.archived || user?.deletion_pending || (user?.approval_status && user.approval_status !== 'accepted')) return <PendingApproval />;

  // Render the main app
  return (
    <PortalProvider user={user}>
      <EquipmentSync user={user} /><UniformSync user={user} />
      <StaffProfileProvider>
        <LayoutWrapper currentPageName={mainPageKey}>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<MainPage />} />
              <Route path="/approve-sign-in" element={<SignInApproval />} />
              <Route path="/event/:id/*" element={<EventDetailsPage />} />
              <Route path="/staff/:id" element={<StaffProfilePage />} />
              {Object.entries(Pages).map(([path, Page]) => (
                <Route key={path} path={`/${path}/*`} element={<Page />} />
              ))}
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </Suspense>
        </LayoutWrapper>
      </StaffProfileProvider>
    </PortalProvider>
  );
};


setupNativeAuthListener();

function AppearanceToaster() {
  const nativeLayout = isAppLayout();
  return <HotToaster position={nativeLayout ? 'top-center' : 'bottom-right'}
    toastOptions={{ style: { background: 'var(--workspace-surface)', color: 'var(--workspace-ink)', border: '1px solid var(--workspace-border)', borderRadius: '8px', fontFamily: 'inherit' } }}
    containerStyle={nativeLayout ? { top: 'calc(env(safe-area-inset-top) + 56px)' } : undefined} />;
}

function App() {

  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => (
      <div className="fixed inset-0 flex items-center justify-center bg-slate-50">
        <div className="text-center p-8">
          <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
          <p className="text-slate-600 mb-4">An unexpected error occurred.</p>
          <button onClick={() => { resetError(); window.location.reload(); }} className="px-4 py-2 bg-[rgb(var(--brand-yellow))] text-black font-semibold rounded hover:bg-[rgb(var(--brand-yellow-hover))]">
            Reload
          </button>
        </div>
      </div>
    )}>
      <AuthProvider>
        <QueryClientProvider client={queryClientInstance}>
          <Router>
            <BrandingProvider>
            <TopLoadingBar />
            <NavigationTracker />
            <NavigateSetter />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/account-deleted" element={<main className="min-h-screen flex flex-col items-center justify-center gap-4 p-6"><h1 className="text-2xl font-semibold">Account deleted</h1><p>Your account has been permanently deleted.</p><a href="/login" className="underline">Return to sign in</a></main>} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/support" element={<Support />} />
              <Route path="/set-password" element={<SetPassword />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="*" element={<AuthenticatedApp />} />
            </Routes>
            <LiveUpdates />
            <AppearanceToaster />
            </BrandingProvider>
          </Router>
          <Toaster />
          <VisualEditAgent />
        </QueryClientProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  )
}

export default App
